/**
 * Die Bauteile der beiden Vorgangslisten (v14, Kapitel 17).
 *
 * Angebote und Aufträge sind dieselbe Maske mit anderen Spalten: oben die
 * Stationen, die zeigen, wo alles steht, und auf Schritt oder Phase filtern;
 * darunter Suche, Sortierhinweis und „Gruppieren ▾"; dann eine Tabelle mit
 * Gruppenköpfen, sortierbaren Spaltenköpfen und ganz rechts dem
 * Vorgangsordner.
 *
 * Eine Umsetzung, nicht zwei: Wer die Angebote bedienen kann, kann die
 * Aufträge bedienen. Zwei nachgebaute Übersichten wären nach dem ersten
 * Nachbessern verschieden – und genau das ist in v12 passiert, als jede
 * Maske ihre eigene Leiste hatte.
 *
 * Farben kommen ausschließlich aus dem Ton der Rubrik (Kapitel 2 und 13);
 * die Auswahl ist Folder Yellow, wie die aktive Kachel auf „Mein Tag".
 */

import { Fragment, useEffect, useRef, useState } from 'react';

import { IconWindowsOrdner } from './Icons';
import type { Ton } from '../typen';
import './liste.css';

/* ---- Stationen ------------------------------------------------------------ */

/**
 * Eine Station der Übersicht „Wo die … stehen" – ein Schritt des Prozesses.
 */
export interface Station {
  key: string;
  /** Fortlaufende Nummer über alle Phasen; „–" für die ruhende Wiedervorlage. */
  nr: string;
  /** Der volle Schrittname, ungekürzt – er darf zweizeilig umbrechen. */
  name: string;
  anzahl: number;
  /** „2 hängen" bzw. „3 kritisch" – nur, wenn es etwas zu melden gibt. */
  warn: string | null;
}

export interface Stationsgruppe {
  key: string;
  label: string;
  ton: Ton;
  /** Wiedervorlage: eigene Gruppe am Ende, gestrichelt, außer der Reihe. */
  ruht?: boolean;
  stationen: Station[];
}

/**
 * Die Übersicht „Wo die … stehen" als Stationen (STATIONEN-LISTEN.md).
 *
 * Vorher war das eine 30-px-Leiste, in der die Breite eines Segments seine
 * Anzahl war. Das ließ sich aus Arbeitsabstand nicht lesen, und ein Schritt
 * mit zwanzig Vorgängen drückte die übrigen zu Strichen zusammen – gerade
 * die leeren, an denen man sieht, wo nichts weitergeht.
 *
 * Jetzt bekommt jeder Schritt eine gleich breite Kachel mit seiner Zahl in
 * 34 px und seinem ganzen Namen. Die Anzahl steht zusätzlich als Balken
 * unten in der Kachel; sie trägt nicht mehr die Breite, sondern eine Länge,
 * die man vergleichen kann, ohne dass sie den Nachbarn Platz wegnimmt.
 *
 * Pfeile zwischen den Kacheln zeigen die Richtung des Prozesses. Eine Phase
 * bricht nur als Ganzes in die nächste Zeile – eine Phase, die in der Mitte
 * zerrissen wird, liest sich wie zwei.
 */
export function Stationen({
  titel,
  gruppen,
  groesste,
  schritt,
  phase,
  hinweis,
  onSchritt,
  onPhase,
  onAlle,
  kinder,
}: {
  titel: string;
  gruppen: Stationsgruppe[];
  /** Die größte Anzahl über alle Schritte – der Maßstab des Balkens. */
  groesste: number;
  schritt: string | null;
  phase: string | null;
  hinweis: string;
  onSchritt: (key: string) => void;
  onPhase: (key: string) => void;
  onAlle: () => void;
  /** Die Zeile unter den Stationen – bei den Angeboten der Personenfilter. */
  kinder?: React.ReactNode;
}) {
  const gefiltert = !!schritt || !!phase;

  return (
    <div className="st-karte">
      <div className="st-kopf">
        <span className="st-titel">{titel}</span>
        <span className="st-kopf-rechts">
          <span className="st-hinweis">{hinweis}</span>
          {gefiltert && (
            <button type="button" className="st-alle" onClick={onAlle}>
              Alle anzeigen
            </button>
          )}
        </span>
      </div>

      <div className="st-gruppen">
        {gruppen.map((g) => {
          const n = g.stationen.length;
          const summe = g.stationen.reduce((x, st) => x + st.anzahl, 0);
          const gewaehlt = g.ruht ? schritt === g.stationen[0]?.key : phase === g.key;
          return (
            <div
              key={g.key}
              className="st-gruppe"
              /*
               * `flex: n 1 …`: Eine Phase mit fünf Schritten wächst fünfmal
               * so schnell wie eine mit einem und bringt die Breite mit, die
               * ihre Kacheln brauchen (STATIONEN-LISTEN 2.2).
               */
              style={{ flex: `${n} 1 ${n * 100 + (n - 1) * 18}px` }}
            >
              <button
                type="button"
                className={`st-phase ${gewaehlt ? 'aktiv' : ''}`}
                title={`${g.label} · ${summe} · Klick filtert auf die ganze Phase`}
                onClick={() => onPhase(g.key)}
              >
                <span className="st-phase-feld" style={{ background: g.ton.akz }} />
                <span className="st-phase-name" style={{ color: g.ton.akzTxt }}>
                  {g.label}
                </span>
                <span className="st-phase-zahl">{summe}</span>
              </button>

              {/*
                Mehr als zwei Kacheln passen auf einem schmalen Bildschirm
                nicht mehr nebeneinander, ohne dass die Namen zu Buchstaben
                werden. Nur solche Gruppen stellen sich dort untereinander –
                eine Phase mit einer Kachel braucht das nie.
              */}
              <div className={`st-kacheln ${n > 2 ? 'st-kacheln--viele' : ''}`}>
                {g.stationen.map((st, j) => {
                  const an = schritt === st.key;
                  const leer = st.anzahl === 0;
                  const gedimmt = (!!schritt && !an) || (!!phase && phase !== g.key);
                  return (
                    /*
                     * Pfeil und Kachel sind Geschwister, nicht ineinander
                     * verschachtelt: Steckte der Pfeil in der Spur der
                     * Kachel, wäre die erste Kachel um seine 18 px breiter
                     * als die übrigen – und „alle Kacheln einer Gruppe
                     * gleich breit" (2.4) wäre nur fast wahr.
                     */
                    <Fragment key={st.key}>
                      {j > 0 && (
                        <span className="st-pfeil" aria-hidden="true">
                          <svg width="9" height="16" viewBox="0 0 9 16">
                            <path
                              d="M2 2l5 6-5 6"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                      )}
                      <button
                        type="button"
                        className={`st-kachel ${an ? 'aktiv' : ''} ${leer ? 'leer' : ''} ${g.ruht ? 'ruht' : ''} ${gedimmt ? 'gedimmt' : ''}`}
                        style={
                          {
                            '--st-akz': g.ton.akz,
                            '--st-txt': g.ton.akzTxt,
                            '--st-tint': g.ton.tint,
                          } as React.CSSProperties
                        }
                        title={`${st.name} · ${st.anzahl}${st.warn ? ' · ' + st.warn : ''}${an ? ' · Filter aufheben' : ' · Liste auf diesen Schritt filtern'}`}
                        onClick={() => onSchritt(st.key)}
                      >
                        <span className="st-zeile">
                          <span className="st-nr">{st.nr}</span>
                          {st.warn && <span className="st-warn">{st.warn}</span>}
                        </span>
                        <span className="st-zahl">{st.anzahl}</span>
                        <span className="st-name">{st.name}</span>
                        <span className="st-fueller" />
                        <span className="st-balken">
                          <span
                            className="st-balken-fuellung"
                            style={{ width: `${Math.round((st.anzahl / Math.max(1, groesste)) * 100)}%` }}
                          />
                        </span>
                      </button>
                    </Fragment>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {kinder}
    </div>
  );
}

/** Eine Filterpille – Phase oder Person. Gewählt ist sie Folder Yellow. */
export function Pille({
  label,
  zahl,
  aktiv,
  punkt,
  zahlWarnt,
  titel,
  onKlick,
}: {
  label: string;
  zahl: number | string;
  aktiv: boolean;
  /** Der Ton der Rubrik – ohne Punkt ist es eine Personenpille. */
  punkt?: string;
  /** Zahl in Critical: in dieser Gruppe hängt etwas. */
  zahlWarnt?: boolean;
  titel?: string;
  onKlick: () => void;
}) {
  return (
    <button
      type="button"
      className={`li-pille ${aktiv ? 'aktiv' : ''}`}
      title={titel}
      onClick={onKlick}
    >
      {punkt && <span className="li-pille-punkt" style={{ background: punkt }} />}
      <span className="li-pille-label">{label}</span>
      <span className={`li-pille-zahl ${zahlWarnt ? 'warnt' : ''}`}>{zahl}</span>
    </button>
  );
}

/* ---- Sortierung ----------------------------------------------------------- */

export type Richtung = 'auf' | 'ab';
export interface Sortierung {
  key: string | null;
  richtung: Richtung;
}

/**
 * Der Klick auf einen Spaltenkopf (v14, 17.5).
 *
 * Erster Klick aufsteigend, zweiter absteigend, dritter hebt auf. Die dritte
 * Stufe ist kein Schmuck: Ohne sie kommt man nie wieder zur Vorgabe zurück,
 * und die ist die einzige Reihenfolge, in der die Liste etwas erzählt.
 */
export function naechsteSortierung(alt: Sortierung, key: string): Sortierung {
  if (alt.key !== key) return { key, richtung: 'auf' };
  if (alt.richtung === 'auf') return { key, richtung: 'ab' };
  return { key: null, richtung: 'auf' };
}

/**
 * Vergleicht zwei Zeilen nach der gewählten Spalte.
 *
 * Leere Werte stehen in beiden Richtungen am Ende (17.5): „kein Termin" ist
 * keine Zeit, und eine Spalte, die mit fünf Strichen beginnt, beantwortet
 * die Frage nicht, für die man sortiert hat.
 */
export function vergleich<T>(
  a: T,
  b: T,
  sort: Sortierung,
  spalten: Record<string, { wert: (x: T) => string | number | null; leer?: (x: T) => boolean }>,
): number {
  if (!sort.key) return 0;
  const s = spalten[sort.key];
  if (!s) return 0;
  const la = s.leer ? s.leer(a) : s.wert(a) === null;
  const lb = s.leer ? s.leer(b) : s.wert(b) === null;
  if (la !== lb) return la ? 1 : -1;
  const x = s.wert(a);
  const y = s.wert(b);
  const r = typeof x === 'string' && typeof y === 'string' ? x.localeCompare(y, 'de') : x === y ? 0 : (x ?? 0) < (y ?? 0) ? -1 : 1;
  return sort.richtung === 'ab' ? -r : r;
}

/** Ein sortierbarer Spaltenkopf. */
export function Spaltenkopf({
  spalte,
  label,
  sort,
  klasse,
  rechts,
  onSort,
}: {
  spalte: string;
  label: string;
  sort: Sortierung;
  klasse?: string;
  rechts?: boolean;
  onSort: (spalte: string) => void;
}) {
  const an = sort.key === spalte;
  return (
    <button
      type="button"
      className={`li-kopfknopf ${an ? 'aktiv' : ''} ${rechts ? 'rechts' : ''} ${klasse ?? ''}`}
      title={
        an
          ? sort.richtung === 'auf'
            ? 'Absteigend sortieren'
            : 'Sortierung aufheben'
          : `Nach ${label} sortieren`
      }
      onClick={() => onSort(spalte)}
    >
      <span>{label}</span>
      <span className="li-sortpfeil">{an ? (sort.richtung === 'auf' ? '▲' : '▼') : ''}</span>
    </button>
  );
}

/* ---- Gruppieren ----------------------------------------------------------- */

/**
 * „Gruppieren ▾" – ein Sekundärknopf mit Menü (v14, 17.6).
 *
 * Kein `<select>`: Das Menü zeigt die gewählte Zeile mit gelber Kante, wie
 * jede andere Auswahl in Kora auch. Ein Systemmenü sähe auf jedem Rechner
 * anders aus und könnte das nicht.
 */
export function Gruppieren({
  optionen,
  wert,
  onWaehlen,
}: {
  optionen: { key: string; label: string }[];
  wert: string;
  onWaehlen: (key: string) => void;
}) {
  const [offen, setOffen] = useState(false);
  const huelle = useRef<HTMLDivElement>(null);

  /* Klick daneben und Esc schließen – wie jedes andere Menü der Anwendung. */
  useEffect(() => {
    if (!offen) return;
    const klick = (e: MouseEvent) => {
      if (!huelle.current?.contains(e.target as Node)) setOffen(false);
    };
    const taste = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOffen(false);
    };
    document.addEventListener('mousedown', klick);
    document.addEventListener('keydown', taste);
    return () => {
      document.removeEventListener('mousedown', klick);
      document.removeEventListener('keydown', taste);
    };
  }, [offen]);

  const label = optionen.find((o) => o.key === wert)?.label ?? '';

  return (
    <div className="li-grupp" ref={huelle}>
      <button
        type="button"
        className="li-gruppknopf"
        aria-expanded={offen}
        onClick={() => setOffen((o) => !o)}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <path d="M4 6h16M4 12h10M4 18h6" />
        </svg>
        <span>Gruppieren · {label}</span>
        <span className="li-gruppknopf-pfeil">▾</span>
      </button>

      {offen && (
        <div className="li-gruppmenue" role="menu">
          {optionen.map((o) => (
            <button
              key={o.key}
              type="button"
              role="menuitemradio"
              aria-checked={o.key === wert}
              className={o.key === wert ? 'aktiv' : ''}
              onClick={() => {
                onWaehlen(o.key);
                setOffen(false);
              }}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Kopf einer Gruppe – Punkt in der Rubrikfarbe, Anzahl, rechts die Summe. */
export function Gruppenkopf({
  label,
  zusatz,
  zusatzTon,
  rechts,
  punkt,
  offen,
  onKlappen,
}: {
  label: string;
  zusatz: string;
  /** „hängt" ist Critical, „beachten" Warning, sonst gedämpft. */
  zusatzTon?: 'krit' | 'warn';
  rechts: string;
  punkt?: string;
  offen: boolean;
  onKlappen: () => void;
}) {
  return (
    <button type="button" className="li-gruppenkopf" onClick={onKlappen}>
      <span className="li-gruppenpfeil">{offen ? '▾' : '▸'}</span>
      {punkt && <span className="li-gruppenpunkt" style={{ background: punkt }} />}
      <span className="li-gruppenlabel">{label}</span>
      <span className={`li-gruppenzusatz ${zusatzTon ?? ''}`}>{zusatz}</span>
      <span className="li-gruppenrechts">{rechts}</span>
    </button>
  );
}

/* ---- Ordner --------------------------------------------------------------- */

/**
 * Die Ordner-Spalte (v14, 17.7).
 *
 * Ein Klick öffnet den Vorgangsordner im Explorer – über das Hilfsprogramm
 * aus dem Profil. Ist es nicht eingerichtet, passiert lautlos nichts;
 * deshalb legt derselbe Klick den Pfad in die Zwischenablage und sagt es.
 * Lieber zweimal Bescheid als einmal ins Leere geklickt.
 */
export function Ordnerzelle({
  art,
  ordnerName,
  onToast,
}: {
  art: 'angebot' | 'auftrag';
  ordnerName: string;
  onToast: (text: string) => void;
}) {
  const angelegt = !!ordnerName;
  const pfad = `${art === 'auftrag' ? 'Aufträge' : 'Angebote'}\\${ordnerName}`;

  return (
    <button
      type="button"
      className="li-ordner"
      title={angelegt ? `Ordner auf dem PC öffnen · ${pfad}` : 'Ordner noch nicht angelegt'}
      aria-label={angelegt ? `Ordner ${ordnerName} öffnen` : 'Ordner noch nicht angelegt'}
      onClick={(e) => {
        e.stopPropagation();
        if (!angelegt) {
          onToast('Für diesen Vorgang ist noch kein Ordner angelegt.');
          return;
        }
        window.location.href = `kora://oeffne?art=${art}&ordner=${encodeURIComponent(ordnerName)}`;
        navigator.clipboard?.writeText(pfad).then(
          () => onToast('Pfad kopiert – falls sich nichts öffnet, im Explorer einfügen.'),
          () => undefined,
        );
      }}
    >
      <IconWindowsOrdner groesse={18} grau={!angelegt} />
    </button>
  );
}

/* ---- Suche ---------------------------------------------------------------- */

export function Listensuche({
  wert,
  onAendern,
}: {
  wert: string;
  onAendern: (w: string) => void;
}) {
  return (
    <label className="li-suche">
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <circle cx="11" cy="11" r="7" />
        <path d="M20 20l-4.3-4.3" />
      </svg>
      <input
        type="search"
        value={wert}
        placeholder="Nummer, Kunde, Ort, Gewerk"
        aria-label="Liste durchsuchen"
        onChange={(e) => onAendern(e.target.value)}
      />
    </label>
  );
}

/* ---- Gewerk --------------------------------------------------------------- */

/**
 * Das Gewerk steckt im Namen: `26014_FE_Weitlofgasse 9` → `FE` (v14, 17.6).
 *
 * Es steht nirgends als eigenes Feld, weil die Nummer es trägt – und die ist
 * der einzige Schlüssel, den alle Systeme des Hauses teilen.
 */
export function gewerkAus(name: string): string {
  return (name.split('_')[1] ?? '').toUpperCase();
}
